import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,

} from "@mui/material";
import { Add, Refresh, Edit, ForkRight } from "@mui/icons-material";

import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import {
  createTermDeposits,
  fetchTermDeposits,
} from "../../redux/masters/termDepositSlice";
import { getOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";
import AddTermDepositModal from "../../components/modals/AddTermDepositModal";
import AddInterestModal from "../../components/modals/AddInterestModal";
import { Percent } from "@mui/icons-material";
import InterestModal from "../../components/modals/AddInterestModal";

const TermDepositMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    items: termDeposits,
    loading,
    error,
  } = useSelector((state) => state.termDeposits);
  const {
    data: loanOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});
  const optionKey = OptionsStatus.TermDeposit;
  const optionData = loanOptions[optionKey] || [];

  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null); // for edit
  const [addLoading, setAddLoading] = useState(false);
  const [openPercentageModal, setOpenPercentageModal] = useState(false);
  const [editingPercentageId, setEditingPercentageId] = useState(null);
  const [percentageRowData, setPercentageRowData] = useState(null);
  // for add

  const [modalState, setModalState] = useState({ open: false, mode: "" }); // ✅ changed
  const [rowData, setRowData] = useState(null); // ✅ changed
  const [loadingPercentId, setLoadingPercentId] = useState(null);
  const [loadingForkId, setLoadingForkId] = useState(null);


  useEffect(() => {
    dispatch(fetchTermDeposits());
  }, [dispatch]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleCloseModal = () => setOpenModal(false);

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureLoanOptionsLoaded();
      setEditData(null);
      setOpenModal(true);
    } catch (err) {
      showError("Failed to load loan options");
    } finally {
      setAddLoading(false);
    }
  };

  const ensureLoanOptionsLoaded = async () => {
    if (!optionData || optionData.length === 0) {
      try {
        await dispatch(getOptions([optionKey])).unwrap();
      } catch (err) {
        showError("Failed to load options");
        throw err; // so the caller can decide what to do
      }
    }
  };

  const handleRefresh = async () => {
    try {
      await dispatch(fetchTermDeposits()).unwrap();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError(error.message || "Failed to refresh data");
    }
  };

  const handleSaveLoanType = async (form) => {
    try {
      await dispatch(createTermDeposits(form)).unwrap();
      await dispatch(fetchTermDeposits()).unwrap();
      showSuccess("Loan type created successfully");
      handleCloseModal();
    } catch (error) {
      showError(error.message || "Failed to create loan type");
    }
  };

  const handleAddInterest = async (newData) => {
    try {
      // 🔹 Example: call API here
      console.log("Saving percentage data:", newData);

      // Removed fetchTermDeposits() to prevent list reload on interest add
      // await dispatch(fetchTermDeposits()).unwrap();

      showSuccess("Percentage added successfully");
    } catch (err) {
      showError("Failed to save percentage");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/masters/deposit"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <AddTermDepositModal
          optionData={optionData}
          optionLoading={optionLoading}
          optionError={optionError}
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          onSave={handleSaveLoanType}
          data={editData}
        />

        {/* ❌ removed old InterestModal that used openPercentageModal */}

        {/* ✅ single InterestModal */}
        <InterestModal
          open={modalState.open}
          mode={modalState.mode}
          onClose={() => {
            setModalState({ open: false, mode: "" });
            setRowData(null);
            setLoadingPercentId(null);
            setLoadingForkId(null);
          }}
          onAdd={handleAddInterest}
          rowData={rowData}
        />

        <Toolbar sx={{ minHeight: 64 }} />
        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Term Deposit Master
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={13} />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={termDeposits || []}
                columns={[
                  {
                    id: "depositCode",
                    label: "Code",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositName",
                    label: "Name",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositType",
                    label: "Deposit Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "depositFrequency",
                    label: "Frequency",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestFrequency",
                    label: "Int. Credit",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestType",
                    label: "Int Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip
                          label="Inactive"
                          size="small"
                          variant="outlined"
                        />
                      ),
                  },
                  {
                    id: "editable",
                    label: "*",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit Term Deposit"
                            : "Data is not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !row.editable || editingLoanId !== null
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEditingLoanId(row.id);
                              try {
                                await ensureLoanOptionsLoaded();
                                setEditData(row);
                                setOpenModal(true);
                              } catch (err) {
                                // handle error
                              } finally {
                                setEditingLoanId(null);
                              }
                            }}
                          >
                            {editingLoanId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Edit fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    id: "actions",
                    label: "Actions",
                    render: (_, row) => (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {/* Percent Icon */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setLoadingPercentId(row.id);
                            setRowData(row);
                            setModalState({ open: true, mode: "percent" });
                          }}
                        >
                          {loadingPercentId === row.id ? <CircularProgress size={20} /> : <Percent fontSize="small" />}
                        </IconButton>

                        {/* ForkRight Icon */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setLoadingForkId(row.id);
                            setRowData({ ...row, showDate: true });
                            setModalState({ open: true, mode: "fork" });
                          }}
                        >
                          {loadingForkId === row.id ? <CircularProgress size={20} /> : <ForkRight fontSize="small" />}
                        </IconButton>
                      </Box>
                    ),
                  }

                ]}
                title="Term Deposits"
                loading={loading}
                emptyMessage="No term deposits found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default TermDepositMaster;
